import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ChevronLeft className="w-5 h-5" />
              <span>Back</span>
            </Link>
            <h1 className="text-xl font-bold text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>
              PRIVACY POLICY
            </h1>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8">
          <p className="text-sm text-slate-500 mb-8">Last updated: February 17, 2026</p>

          <div className="prose prose-slate max-w-none">
            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>1. INTRODUCTION</h2>
            <p className="text-slate-600 mb-6">
              Anglin Sports Consultancy ("Company", "we", "us", or "our") operates Schedule Guru (the "Service"). This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our Service. Please read this 
              Privacy Policy carefully. By using the Service, you consent to the practices described in this policy.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>2. INFORMATION WE COLLECT</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mb-3">Personal Information</h3>
            <p className="text-slate-600 mb-4">We may collect the following personal information:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Name and email address (when you create an account)</li>
              <li>Profile picture (if you sign in with Google)</li>
              <li>Payment information (processed securely through our payment provider)</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-3">Usage Information</h3>
            <p className="text-slate-600 mb-4">We automatically collect certain information when you use the Service:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Device information (browser type, operating system)</li>
              <li>IP address and general location data</li>
              <li>Pages visited and features used</li>
              <li>Date and time of access</li>
              <li>Referring website addresses</li>
            </ul>

            <h3 className="text-lg font-semibold text-slate-800 mb-3">User-Generated Content</h3>
            <p className="text-slate-600 mb-6">
              We collect and store content you create using our Service, including tournament names, team names, schedules, 
              and any other data you input into the platform.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>3. HOW WE USE YOUR INFORMATION</h2>
            <p className="text-slate-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Create and manage your account</li>
              <li>Send administrative information, updates, and promotional materials</li>
              <li>Respond to your comments, questions, and support requests</li>
              <li>Monitor and analyze usage patterns and trends</li>
              <li>Detect, prevent, and address technical issues and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>4. INFORMATION SHARING AND DISCLOSURE</h2>
            <p className="text-slate-600 mb-4">We may share your information in the following circumstances:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Service Providers:</strong> With third-party vendors who assist us in providing the Service (e.g., payment processors, hosting providers)</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, or other legal process</li>
              <li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property, or that of our users or the public</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>With Your Consent:</strong> When you have given us explicit permission to share your information</li>
            </ul>
            <p className="text-slate-600 mb-6">
              We do not sell, rent, or trade your personal information to third parties for marketing purposes.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>5. DATA RETENTION</h2>
            <p className="text-slate-600 mb-6">
              We retain your personal information for as long as your account is active or as needed to provide you with our Service. 
              We may also retain and use your information to comply with legal obligations, resolve disputes, and enforce our agreements. 
              Upon account deletion, we will delete or anonymize your data within 90 days, except where retention is required by law.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>6. DATA SECURITY</h2>
            <p className="text-slate-600 mb-6">
              We implement appropriate technical and organizational security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure servers, 
              and access controls. However, no method of transmission over the Internet or electronic storage is 100% secure, 
              and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>7. YOUR RIGHTS AND CHOICES</h2>
            <p className="text-slate-600 mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing of your personal information</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-slate-600 mb-6">
              To exercise these rights, please contact us at info@scheduleguru.ws. We will respond to your request within 30 days.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>8. COOKIES AND TRACKING TECHNOLOGIES</h2>
            <p className="text-slate-600 mb-4">We use cookies and similar tracking technologies to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-6 space-y-2">
              <li>Maintain your session and remember your preferences</li>
              <li>Analyze usage patterns and improve our Service</li>
              <li>Provide secure authentication</li>
            </ul>
            <p className="text-slate-600 mb-6">
              You can control cookies through your browser settings. Note that disabling cookies may affect the functionality of our Service.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>9. THIRD-PARTY SERVICES</h2>
            <p className="text-slate-600 mb-6">
              Our Service may contain links to third-party websites or integrate with third-party services (such as Google Sign-In 
              and payment processors). We are not responsible for the privacy practices of these third parties. We encourage you 
              to review their privacy policies before providing any personal information.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>10. CHILDREN'S PRIVACY</h2>
            <p className="text-slate-600 mb-6">
              Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from 
              children under 13. If you are a parent or guardian and believe your child has provided us with personal information, 
              please contact us immediately.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>11. INTERNATIONAL DATA TRANSFERS</h2>
            <p className="text-slate-600 mb-6">
              Your information may be transferred to and processed in countries other than your country of residence. These countries 
              may have different data protection laws. By using our Service, you consent to the transfer of your information to these 
              countries. We take appropriate safeguards to ensure your information remains protected.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>12. CHANGES TO THIS PRIVACY POLICY</h2>
            <p className="text-slate-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date. Your continued use of the Service after such changes 
              constitutes acceptance of the new Privacy Policy.
            </p>

            <h2 className="text-2xl font-bold text-[#00247D] mb-4" style={{ fontFamily: 'Barlow Condensed' }}>13. CONTACT US</h2>
            <p className="text-slate-600 mb-6">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:<br />
              <strong>Anglin Sports Consultancy</strong><br />
              Email: info@scheduleguru.ws<br />
              Cayman Islands
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
